import { User as OidcUser } from "oidc-client-ts";
import User from "@/interfaces/permission/user";
import { AppRole, getAppRoles } from "@/service/AppRoleService";
import Logger from "@/utils/Logger";
import { AppSettingsService } from "./AppSettingsService";

/**
 * Basic authenticated user profile.
 */
interface AuthUser {
  name: string;
  email: string;
}

/**
 * Service for handling authentication and user profile extraction.
 */
export class AuthService {

  /**
   * Extracts basic user profile from the OIDC user.
   * @param oidcUser
   */
  static extractUserProfile(oidcUser: OidcUser): AuthUser {
    Logger.debug("Extracting user profile from OIDC user", ["AuthService"]);

    const profile = {
      name: oidcUser.profile.name || oidcUser.profile.given_name || "",
      email: oidcUser.profile.email || "",
    };

    Logger.info("User profile extracted successfully", ["AuthService"]);
    return profile;
  }

  /**
   * Extracts detailed user info from the OIDC user.
   * @param oidcUser
   */
  static extractUserInfo(oidcUser: OidcUser): User {
    Logger.debug("Extracting detailed user info from OIDC user", [
      "AuthService",
    ]);

    const userInfo: User = {
      address:
          oidcUser.profile.address && oidcUser.profile.address.country
              ? { country: oidcUser.profile.address.country }
              : undefined,
      appRoles: getAppRoles(oidcUser),
      displayName:
          oidcUser.profile.name ||
          `${oidcUser.profile.given_name} ${oidcUser.profile.family_name}`.trim(),
      destinationIndicator: oidcUser.profile.destinationIndicator as
          | string
          | undefined,
      employeeID: oidcUser.profile.employeeID as string | undefined,
      employeeType: oidcUser.profile.employeeType as string | undefined,
      family_name: oidcUser.profile.family_name,
      given_name: oidcUser.profile.given_name,
      sub: oidcUser.profile.sub,
    };

    Logger.info("User info extracted successfully", ["AuthService"]);
    return userInfo;
  }

  /**
   * Extracts application roles from the OIDC user.
   * @param oidcUser
   */
  static extractAppRoles(oidcUser: OidcUser): AppRole[] {
    Logger.debug("Extracting app roles from OIDC user", ["AuthService"]);
    const roles = getAppRoles(oidcUser);
    Logger.info(`App roles extracted: ${roles.length} roles found`, [
      "AuthService",
    ]);
    return roles;
  }

  /**
   * Gets development user for local testing.
   */
  static getDevelopmentUser(): AuthUser {
    Logger.warn(
        "Development mode: Using mock user - bypassing OIDC authentication",
        ["AuthService"],
    );
    return {
      name: "Local Dev User",
      email: "localdev@example.com",
    };
  }

  /**
   * Gets development user info for local testing.
   */
  static getDevelopmentUserInfo(): User {
    Logger.warn(
        "Development mode: Using mock user profile with admin privileges",
        ["AuthService"],
    );
    return {
      appRoles: [AppRole.SCRM_APP_ADMIN],
      displayName: "Local Dev User",
      employeeID: "DEV001",
      employeeType: "Developer",
      family_name: "User",
      given_name: "Local",
      sub: "dev-user-123",
    };
  }

  /**
   * Gets development app roles for local testing.
   */
  static getDevelopmentAppRoles(): AppRole[] {
    Logger.warn(
        "Development mode: Granting full admin permissions for local testing",
        ["AuthService"],
    );
    return [AppRole.SCRM_APP_ADMIN];
  }

  /**
   * Checks if the user is authenticated.
   * @param user
   */
  static isAuthenticated(user: OidcUser | null): boolean {
    const oauthBypassEnabled =
        AppSettingsService.isOAuthLocalHostBypassEnabled();
    const isLocalhost = window.location.hostname === "localhost";

    if (!oauthBypassEnabled && isLocalhost) {
      Logger.debug(
          "Development mode: OAuth bypass enabled - Bypassing authentication check for localhost and auto-authenticating for localhost",
          ["AuthService"],
      );
      return true;
    }

    const isAuth = !!(user && !user.expired);
    Logger.debug(
        `${isLocalhost ? "Development mode with OAuth enforcement" : "Production authentication check"}: ${isAuth ? "authenticated" : "not authenticated"}`,
        ["AuthService"],
    );
    return isAuth;
  }

  /**
   * Performs sign-in via OIDC.
   * @param signinRedirect
   */
  static async performSignIn(
      signinRedirect: () => Promise<void>,
  ): Promise<void> {
    Logger.info("Initiating OIDC sign-in process", ["AuthService"]);
    try {
      await signinRedirect();
      Logger.info("OIDC sign-in redirect initiated successfully", [
        "AuthService",
      ]);
    } catch (err) {
      Logger.error("OIDC sign-in process failed", err, ["AuthService"]);
      throw err;
    }
  }

  /**
   * Performs sign-out via OIDC.
   * @param signoutRedirect
   */
  static async performSignOut(
      signoutRedirect: () => Promise<void>,
  ): Promise<void> {
    Logger.info("Initiating OIDC sign-out process", ["AuthService"]);
    try {
      await signoutRedirect();
      Logger.info("OIDC sign-out redirect initiated successfully", [
        "AuthService",
      ]);
    } catch (err) {
      Logger.error("OIDC sign-out process failed", err, ["AuthService"]);
      throw err;
    }
  }

  /**
   * Performs silent sign-in to renew tokens.
   * @param signinSilent
   */
  static async performSilentSignIn(
      signinSilent: () => Promise<OidcUser | null>,
  ): Promise<void> {
    Logger.info("Attempting silent token renewal via OIDC", ["AuthService"]);
    try {
      const user = await signinSilent();
      Logger.info(
          `Silent token renewal completed: ${user ? "successful" : "failed"}`,
          ["AuthService"],
      );
    } catch (err) {
      Logger.error("Silent token renewal failed", err, ["AuthService"]);
      throw err;
    }
  }

  /**
   * Clears stale authentication state.
   * @param clearStaleState
   */
  static async performClearStaleState(
      clearStaleState: () => Promise<void>,
  ): Promise<void> {
    try {
      Logger.info("Clearing stale state", ["AuthService"]);
      await clearStaleState();
      Logger.info("Stale state cleared", ["AuthService"]);
    } catch (err) {
      Logger.error("Clearing stale state failed", err, ["AuthService"]);
      throw err;
    }
  }

  /**
   * Checks if the user has the required role.
   * @param userRoles
   * @param requiredRole
   */
  static hasRole(userRoles: AppRole[], requiredRole: AppRole): boolean {
    const hasRole = userRoles.includes(requiredRole);
    Logger.debug(
        `Role check: ${requiredRole} - ${hasRole ? "granted" : "denied"}`,
        ["AuthService"],
    );
    return hasRole;
  }

  /**
   * Checks if the user has any of the required roles.
   * @param userRoles
   * @param requiredRoles
   */
  static hasAnyRole(userRoles: AppRole[], requiredRoles: AppRole[]): boolean {
    const hasAnyRole = requiredRoles.some((role) => userRoles.includes(role));
    Logger.debug(`Multi-role check: ${hasAnyRole ? "granted" : "denied"}`, [
      "AuthService",
    ]);
    return hasAnyRole;
  }
  
  /**
   * Checks if the user has admin access based on their roles.
   * @param userRoles
   */
  static isAdmin(userRoles: AppRole[]): boolean {
    const isAdmin = this.hasRole(userRoles, AppRole.SCRM_APP_ADMIN);
    Logger.debug(`Admin check: ${isAdmin ? "is admin" : "not admin"}`, [
      "AuthService",
    ]);
    return isAdmin;
  }

  /**
   * Checks if the user has read-only access based on their roles.
   * @param userRoles
   */
  static isReadOnlyUser(userRoles: AppRole[]): boolean {
    const isReadOnly = this.hasRole(userRoles, AppRole.SCRM_APP_READ_ONLY);
    Logger.debug(
        `Read-only check: ${isReadOnly ? "read-only" : "not read-only"}`,
        ["AuthService"],
    );
    return isReadOnly;
  }
  
  /**
   * Flag to track if redirect was already consumed this session
   */
  private static hasConsumedRedirect = false;
  
  /**
   * Consumes the saved post-login redirect URL from sessionStorage.
   * @returns {string} The saved URL or the root path if none exists.
   */
  static consumePostLoginRedirect(): string {
    try {
      if (this.hasConsumedRedirect) {
        Logger.debug("Redirect already consumed this session, returning default", [
          "AuthService",
        ]);
        return "/";
      }
  
      const redirectUrl = sessionStorage.getItem("postLoginRedirect") || "/";
      sessionStorage.removeItem("postLoginRedirect");
  
      this.hasConsumedRedirect = true;
      Logger.info(`Post-login redirect URL consumed: ${redirectUrl}`, ["AuthService"]);
  
      return redirectUrl;
    } catch (e) {
      Logger.error("Failed to consume post-login redirect URL", e, ["AuthService"]);
      return "/";
    }
  }
  
  /**
   * Resets the consumption flag (call on logout)
   */
  static resetRedirectConsumption(): void {
    this.hasConsumedRedirect = false;
  }
  
  /**
   * Saves the current page URL to sessionStorage for post-login redirection.
   * Only saves if the path is valid and not already saved.
   */
  static savePostLoginRedirect(): void {
    try {
      // Prevent saving if already exists
      if (sessionStorage.getItem("postLoginRedirect")) {
        Logger.debug("Post-login redirect already saved, skipping", ["AuthService"]);
        return;
      }
  
      const currentPath = window.location.pathname + window.location.search;
      
      if (this.validatePath(currentPath)) {
        sessionStorage.setItem("postLoginRedirect", currentPath);
        Logger.info(`Post-login redirect path saved: ${currentPath}`, ["AuthService"]);
      } else {
        Logger.warn("Invalid redirect path, skipping save", ["AuthService"]);
      }
    } catch (error) {
      Logger.error("Failed to save post-login redirect path", error, ["AuthService"]);
    }
  }
  
  /**
   * Validates if the given path is within the same origin and safe for redirect.
   * @param path - The path to validate.
   * @returns {boolean} - True if valid, false otherwise.
   */
  static validatePath(path: string): boolean {
    try {
      // Prevent saving login/logout pages
      const unsafePaths = ["/login", "/logout", "/auth/callback", "/auth/silent-callback"];
      if (unsafePaths.some(p => path.startsWith(p))) {
        Logger.debug(`Path ${path} is unsafe for redirect`, ["AuthService"]);
        return false;
      }
  
      const url = new URL(path, window.location.origin);
      return url.origin === window.location.origin;
    } catch {
      return false;
    }
  }
  
  /**
   * Forces a redirect to the login page after saving the current path.
   * Used when silent renew fails or token is invalid.
   */
  static forceLoginRedirect(signinRedirect: () => Promise<void>): void {
    try {
      Logger.info("Forcing redirect to login page", ["AuthService"]);
      
      // Save current path (will skip if already saved)
      this.savePostLoginRedirect();
      
      // Clear any stale OIDC state
      sessionStorage.removeItem(`oidc.user:${window.location.origin}`);      
      // Force redirect to login
      signinRedirect().catch((err) => {
        Logger.error("Failed to redirect to login", err, ["AuthService"]);
        // Fallback: manual navigation to authority
        window.location.href = AppSettingsService.getOpenIdAuthority();
      });
    } catch (error) {
      Logger.error("Critical error during force login redirect", error, ["AuthService"]);
      // Last resort: reload the page
      window.location.reload();
    }
  }
}
